import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, errMsg, nextSaturday } from "@/lib/api";
import { DatePickerButton } from "@/components/DatePickerButton";
import { TipSheetPreview } from "@/components/sheet/TipSheetPreview";

const SheetCard = ({ s, onChange }) => {
  const api = useApi();
  const id = s.track.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const act = async (what) => {
    try { onChange(await api("post", `/admin/sheets/${s.meeting_id}/${what}`)); toast.success(what === "approve" ? "Sheet approved and shown on the Tips page" : "Sheet back to draft"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li className="rounded-xl border border-white/5 bg-[#0B101D] p-3 space-y-3" data-testid={`sheet-card-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold">{s.track}</p>
        <span className={`text-[10px] uppercase tracking-widest border rounded px-1.5 py-0.5 ${s.status === "approved" ? "text-emerald-300 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`} data-testid={`sheet-status-${id}`}>{s.status}</span>
        {s.status === "approved"
          ? <button data-testid={`sheet-unapprove-${id}`} onClick={() => act("unapprove")} className="ml-auto px-3 py-1 rounded-full border border-white/10 text-xs text-slate-300">Back to draft</button>
          : <button data-testid={`sheet-approve-${id}`} onClick={() => act("approve")} disabled={!s.ready} className="ml-auto px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold disabled:opacity-40">Approve sheet</button>}
      </div>
      {s.status !== "approved" && (
        <p className="text-xs text-slate-400" data-testid={`sheet-waiting-${id}`}>
          {s.ready ? "Every race has published tips. Approve to freeze the sheet with those tips and prices." : `Shows the bot's draft picks. Publish tips for ${s.waiting.map((n) => `R${n}`).join(", ")} before approving.`}
        </p>
      )}
      <TipSheetPreview sheet={s} />
      {s.caption && <pre className="text-xs text-slate-300 whitespace-pre-wrap bg-[#131C31] rounded-lg p-2" data-testid={`sheet-caption-${id}`}>{s.caption}</pre>}
    </li>
  );
};

export const SheetsPanel = () => {
  const api = useApi();
  const [date, setDate] = useState(nextSaturday());
  const [sheets, setSheets] = useState(null);
  const load = useCallback(() => api("get", `/admin/sheets?date=${date}`).then(setSheets).catch((e) => toast.error(errMsg(e))), [api, date]);
  useEffect(() => { load(); }, [load]);
  const build = async () => {
    try { const r = await api("post", "/admin/sheets/build", { date }); toast.success(r.created ? `${r.created} draft sheet${r.created > 1 ? "s" : ""} created` : "Every metro meeting already has a sheet"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/5 p-5 space-y-4" data-testid="sheets-panel">
      <div>
        <h2 className="text-lg font-bold">Saturday tip sheets</h2>
        <p className="text-xs text-slate-400">One sheet per metro meeting, drafted by the 6am Saturday run. Approve each one to show it on the Tips page.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <DatePickerButton value={date} onChange={setDate} testid="sheets-date" />
        <button data-testid="sheets-build" onClick={build} className="px-4 py-1.5 rounded-full bg-blue-600 hover:bg-blue-500 text-xs font-semibold">Build sheets now</button>
      </div>
      <ul className="space-y-3">
        {sheets && !sheets.length && <li className="text-xs text-slate-500" data-testid="sheets-empty">No sheets for this day yet.</li>}
        {sheets?.map((s) => <SheetCard key={s.id} s={s} onChange={(n) => setSheets((x) => x.map((y) => (y.id === n.id ? n : y)))} />)}
      </ul>
    </section>
  );
};
